import { type ReactNode, useEffect, useState } from 'react';
import sprite from '@launchpad-ui/icons/sprite.svg';

// Import the vital sprite file path
import vitalSpriteUrl from '../../../images/icons/vital-app-sprite.svg';

type IconProviderProps = {
  children: ReactNode;
};

type LoadingState = {
  launchpadLoaded: boolean;
  vitalLoaded: boolean;
  error: Error | null;
};

const loadLaunchpadSprite = (): void => {
  // Check if already loaded to prevent duplicates
  if (document.getElementById('lp-icons-sprite')) {
    return;
  }

  const parser = new DOMParser();
  const lpContent = parser.parseFromString(sprite, 'image/svg+xml').documentElement;

  // Check for parsing errors
  const parserError = lpContent.querySelector('parsererror');
  if (parserError) {
    throw new Error('Failed to parse LaunchPad sprite SVG');
  }

  lpContent.id = 'lp-icons-sprite';
  lpContent.style.display = 'none';
  document.body.appendChild(lpContent);
};

const loadVitalSprite = async (): Promise<void> => {
  // Check if already loaded to prevent duplicates
  if (document.getElementById('lp-app-icons-sprite')) {
    return;
  }

  const response = await fetch(vitalSpriteUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch Vital sprite: ${response.status} ${response.statusText}`);
  }

  const data = await response.text();
  const vitalParser = new DOMParser();
  const content = vitalParser.parseFromString(data, 'image/svg+xml').documentElement;

  // Check for parsing errors
  const parserError = content.querySelector('parsererror');
  if (parserError) {
    throw new Error('Failed to parse Vital sprite SVG');
  }

  content.id = 'lp-app-icons-sprite';
  content.style.display = 'none';
  document.body.appendChild(content);
};

export function IconProvider({ children }: IconProviderProps) {
  const [loadingState, setLoadingState] = useState<LoadingState>({
    launchpadLoaded: false,
    vitalLoaded: false,
    error: null,
  });

  useEffect(() => {
    const loadSprites = async () => {
      try {
        // Load LaunchPad sprites synchronously
        loadLaunchpadSprite();
        setLoadingState((prev) => ({ ...prev, launchpadLoaded: true }));

        // Load Vital sprites asynchronously
        await loadVitalSprite();
        setLoadingState((prev) => ({ ...prev, vitalLoaded: true }));
      } catch (err) {
        console.error('Failed to load icon sprites:', err);
        setLoadingState((prev) => ({
          ...prev,
          error: err instanceof Error ? err : new Error('Unknown sprite loading error'),
        }));
      }
    };

    loadSprites();
  }, []);

  // If there's an error, throw it to be caught by error boundaries
  if (loadingState.error) {
    throw loadingState.error;
  }

  // Only render children once both LaunchPad and Vital sprites are loaded
  // This prevents race conditions where components try to use Vital icons before they're available
  if (!loadingState.launchpadLoaded || !loadingState.vitalLoaded) {
    return null;
  }

  return <>{children}</>;
}
